import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Logo from "../AllImages/MainLogo.png";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { Link } from "react-router-dom";

const DownPaymentPage = ({ uid, name }) => {
  const [reservationData, setReservationData] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [expanded, setExpanded] = useState([]); // Tracks which participant is expanded
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false); // Track payment completion

  const stripe = useStripe();
  const elements = useElements();

  // Fetch reservation data from Firestore
  useEffect(() => {
    if (!uid) {
      setError("No UID provided.");
      setLoading(false);
      return;
    }
    const fetchReservation = async () => {
      try {
        const docRef = doc(db, "reservations", uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setReservationData(data);
          // Initialize participants and expanded states
          const parts = data.participants || [];
          setParticipants(parts);
          setExpanded(Array(parts.length).fill(false));

          // If downPayment is true in Firestore, mark as paid
          if (data.downPayment === true) {
            setPaymentSuccess(true);
          }
        } else {
          setError("Reservation not found.");
        }
      } catch (err) {
        console.error("Error fetching reservation data:", err);
        setError("Error fetching reservation data.");
      } finally {
        setLoading(false);
      }
    };
    fetchReservation();
  }, [uid]);

  // Handlers to manage participants (add, delete, update)
  const handleParticipantChange = (index, field, value) => {
    const updatedParticipants = [...participants];
    updatedParticipants[index] = { ...updatedParticipants[index], [field]: value };
    setParticipants(updatedParticipants);
  };

  const handleDeleteParticipant = (index) => {
    const updatedParticipants = participants.filter((_, i) => i !== index);
    setParticipants(updatedParticipants);
    const updatedExpanded = expanded.filter((_, i) => i !== index);
    setExpanded(updatedExpanded);
  };

  const handleAddParticipant = () => {
    const newParticipant = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      age: "",
      role: "",
      instrument: "",
      nationality: "",
      address: "",
      dietary: "",
      singleBed: false,
    };
    setParticipants([...participants, newParticipant]);
    setExpanded([...expanded, true]); // Automatically expand the new participant form
  };

  const toggleExpanded = (index) => {
    const newExpanded = [...expanded];
    newExpanded[index] = !newExpanded[index];
    setExpanded(newExpanded);
  };

  // Compute total deposit: $550 per participant, plus extra $150 if single bed is selected
  const totalDeposit = participants.reduce(
    (acc, p) => acc + 550 + (p.singleBed ? 150 : 0),
    0
  );

  // Handle down payment using Stripe integration
  const handlePayment = async () => {
    if (!stripe || !elements) {
      alert("Stripe has not loaded yet. Please try again later.");
      return;
    }

    setPaymentLoading(true);
    try {
      // Since the backend multiplies by 100, we just pass totalDeposit in dollars
      const amountToCharge = totalDeposit;

      // Logs to check the values
      console.log("Participants:", participants);
      console.log("Calculated totalDeposit (in dollars):", totalDeposit);

      const paymentData = {
        amount: amountToCharge,  // plain dollars; backend does the *100
        uid,
        name,
        // Use primaryContact.email from reservationData if available
        email: reservationData?.primaryContact?.email || "",
        totalDeposit,
        participants,
      };

      // Hitting the route that multiplies by 100 in the backend
      const response = await fetch(
        "https://us-central1-newyorktour-33c8c.cloudfunctions.net/api/createPaymentIntent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentData),
        }
      );

      if (!response.ok) {
        alert("Error creating Payment Intent. Please try again.");
        setPaymentLoading(false);
        return;
      }

      const result = await response.json();
      const clientSecret = result.clientSecret;
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        alert("Card element not found. Please try again.");
        setPaymentLoading(false);
        return;
      }

      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: { name },
          },
        });

      if (confirmError) {
        console.error("Error confirming card payment:", confirmError);
        alert("Error confirming payment. Please try again.");
        setPaymentLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Update Firestore:
        // 1) Mark downPayment = true
        // 2) Save any new changes to participants (like singleBed, names, etc.)
        await updateDoc(doc(db, "reservations", uid), {
          downPayment: true,
          participants: participants,
        });

        alert("Payment successful!");
        setPaymentSuccess(true);
      }
    } catch (err) {
      console.error("Payment error:", err);
      alert("There was an error processing your payment. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Loading reservation data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  // SUCCESS SCREEN: Displayed when down payment is complete
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-6">
        <motion.div
          className="p-8 bg-green-800 rounded-xl text-center shadow-2xl border border-green-600"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-4xl font-bold mb-4">🎉 Payment Completed! 🎉</h2>
          <p className="text-xl mb-4">Thank you for completing your down payment.</p>
          <p className="text-lg mb-6">
            Please proceed to the Monthly Payment Page to keep up with your monthly payments.
          </p>
          {/* Adjust the route as needed */}
          <h1 className="bg-yellow-400 hover:bg-yellow-300 text-black px-6 py-3 rounded font-semibold transition-colors">
            Go to The monthly Page
          </h1>
        </motion.div>
      </div>
    );
  }

  // MAIN DOWN PAYMENT PAGE: Display the payment form if payment is not yet complete.
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col p-4 md:p-8">
      {/* Congratulatory & Tour Hype Section */}
      <motion.div
  className="mb-10 p-10 bg-gradient-to-r from-green-600 to-green-800 rounded-3xl text-white text-center shadow-2xl border-2 border-green-500"
  initial={{ opacity: 0, scale: 0.95 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.7 }}
>
  <h2 className="text-5xl font-extrabold mb-6">
    🎉 Congratulations! 🎉
  </h2>
  
  <p className="mt-2 text-2xl font-medium leading-relaxed">
    You’ve passed your audition and have been <span className="text-yellow-300 font-bold">welcomed</span> to the 
    <span className="text-yellow-300 font-bold"> Simanof New York Tour 2025</span>! 🚀🌟
  </p>
  
  <p className="mt-4 text-2xl font-medium leading-relaxed">
    Immerse yourself in an All-Inclusive 5-day New York experience! Your itinerary is packed with once-in-a-lifetime tours and activities:
  </p>
  
  <ul className="mt-4 text-2xl font-medium leading-relaxed list-disc list-inside">
    <li>
      <span className="text-yellow-300 font-semibold">Statue of Liberty</span>
    </li>
    <li>
      <span className="text-yellow-300 font-semibold">NY Masterclass</span>
    </li>
    <li>
      <span className="text-yellow-300 font-semibold">Music Competition</span>
    </li>
    <li>
      <span className="text-yellow-300 font-semibold">Christmas Market Event</span>
    </li>
    <li>
      <span className="text-yellow-300 font-semibold">Empire State</span>
    </li>
    <li>and many more spectacular experiences!</li>
  </ul>
  
  <p className="mt-6 text-2xl font-medium">
    Total tour cost: <span className="text-yellow-300 font-bold">$1,899.99</span>
  </p>
  
  <p className="mt-2 text-2xl font-medium">
    Secure your spot with an initial deposit of <span className="text-yellow-300 font-bold">$550</span>
  </p>
  
  <p className="mt-2 text-2xl font-medium">
    and then monthly installments of <span className="text-yellow-300 font-bold">$192.85 + Tax</span>.
  </p>

  {/* Additional Details */}

  <p className="mt-4 text-xl italic">
    <strong>Tour Dates & Logistics:</strong> Mark your calendars for an unforgettable journey from <span className="text-yellow-300 font-bold">December 15–20</span>.
  </p>
  
  <p className="mt-4 text-xl italic">
    <strong>All-Inclusive Details:</strong> Your package includes meals, lodging, and entry to all scheduled events. Note that personal expenses such as tips, souvenirs, or extra baggage fees are not covered.
  </p>
  
  <p className="mt-4 text-xl italic">
    <strong>Tax Information:</strong> The monthly installment of <span className="text-yellow-300 font-bold">$192.85</span> is subject to additional local taxes, which will be clearly outlined in your final invoice.
  </p>
  
  <p className="mt-4 text-xl italic">
    <strong>Next Steps:</strong> After you click "Complete Payment," you will receive a confirmation email along with detailed instructions to finalize your registration.
  </p>
  
  <p className="mt-4 text-xl italic">
    <strong>A Word from the Simanof Foundation:</strong> Our renowned music faculty and dedicated team are excited to welcome you aboard. Join a legacy of past participants and embark on a journey filled with inspiration and success!
  </p>
  
  <p className="mt-6 text-2xl font-medium">
    Complete your payment below to embark on this extraordinary journey! 🎶🗽
  </p>
</motion.div>


      {/* Main Payment Form Container */}
      <motion.div
        className="max-w-5xl w-full mx-auto bg-gray-800 rounded-lg shadow-xl p-6"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {/* Manage Participants Section */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-yellow-400 mb-4">
            Manage Participants
          </h3>
          {participants.map((participant, index) => (
            <div
              key={index}
              className="bg-gray-700 p-4 rounded-lg mb-4 border border-gray-600 transition-all"
            >
              {/* Participant Header */}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-full bg-yellow-500 flex items-center justify-center text-gray-900 font-semibold">
                    {index + 1}
                  </div>
                  <h4 className="text-lg text-white font-semibold">
                    Participant {index + 1}
                  </h4>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => toggleExpanded(index)}
                    className="text-white hover:text-yellow-300 transition-colors"
                  >
                    {expanded[index] ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  <button
                    onClick={() => handleDeleteParticipant(index)}
                    className="text-red-400 hover:text-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Expanded Participant Form */}
              {expanded[index] && (
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div>
                    <label className="block text-gray-200 mb-1">First Name</label>
                    <input
                      type="text"
                      value={participant.firstName}
                      onChange={(e) =>
                        handleParticipantChange(index, "firstName", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={participant.lastName}
                      onChange={(e) =>
                        handleParticipantChange(index, "lastName", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Email</label>
                    <input
                      type="email"
                      value={participant.email}
                      onChange={(e) =>
                        handleParticipantChange(index, "email", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Phone</label>
                    <input
                      type="text"
                      value={participant.phone}
                      onChange={(e) =>
                        handleParticipantChange(index, "phone", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Age</label>
                    <input
                      type="number"
                      value={participant.age}
                      onChange={(e) =>
                        handleParticipantChange(index, "age", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Role</label>
                    <select
                      value={participant.role}
                      onChange={(e) =>
                        handleParticipantChange(index, "role", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    >
                      <option value="">Select Role</option>
                      <option value="chaperone">Chaperone</option>
                      <option value="musician">Musician</option>
                    </select>
                  </div>
                  {participant.role === "musician" && (
                    <div>
                      <label className="block text-gray-200 mb-1">Instrument</label>
                      <input
                        type="text"
                        value={participant.instrument}
                        onChange={(e) =>
                          handleParticipantChange(index, "instrument", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-gray-200 mb-1">Nationality</label>
                    <input
                      type="text"
                      value={participant.nationality}
                      onChange={(e) =>
                        handleParticipantChange(index, "nationality", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Address</label>
                    <input
                      type="text"
                      value={participant.address}
                      onChange={(e) =>
                        handleParticipantChange(index, "address", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-200 mb-1">Dietary Restrictions</label>
                    <input
                      type="text"
                      value={participant.dietary}
                      onChange={(e) =>
                        handleParticipantChange(index, "dietary", e.target.value)
                      }
                      className="border p-2 rounded w-full bg-gray-50 text-gray-900"
                    />
                  </div>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={participant.singleBed}
                      onChange={(e) =>
                        handleParticipantChange(index, "singleBed", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-gray-200">
                      Single Bed Option (Extra $150)
                    </label>
                  </div>
                </motion.div>
              )}
            </div>
          ))}

          {/* Payment Breakdown */}
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-yellow-400 mb-2">
              Participants Payment Breakdown
            </h3>
            <ul className="ml-4 list-disc">
              {participants.map((p, index) => {
                const baseDeposit = 550;
                const extra = p.singleBed ? 150 : 0;
                const displayName =
                  p.firstName || p.lastName
                    ? `${p.firstName} ${p.lastName}`.trim()
                    : `Participant ${index + 1}`;
                return (
                  <li key={index} className="text-gray-200 mb-1">
                    {displayName}: ${baseDeposit}
                    {p.singleBed && <span> + ${extra} for Single Bed</span>}
                  </li>
                );
              })}
            </ul>
          </div>

          <p className="text-gray-100 mb-6">
            Total Deposit:{" "}
            <span className="font-bold text-yellow-300">${totalDeposit}</span>
          </p>

          {/* Payment Information Section using Stripe's CardElement */}
          <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
            <h3 className="text-xl font-semibold text-yellow-400 mb-4">
              Credit Card Information
            </h3>
            <div className="bg-gray-800 p-4 rounded mb-4">
              <CardElement
                options={{
                  style: { base: { fontSize: "16px", color: "#fff" } },
                }}
              />
            </div>
          </div>

          {/* Pay Now Button */}
          <div className="mt-8 flex justify-center">
            <button
              className="bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-3 rounded font-bold text-2xl transition-colors"
              onClick={handlePayment}
              disabled={paymentLoading}
            >
              {paymentLoading ? "Processing..." : "Pay Now"}
            </button>
          </div>
        </div> {/* Closing the Manage Participants Section */}
      </motion.div>
    </div>
  );
};

export default DownPaymentPage;
